/**
 * The drawing surface for a signature or set of initials.
 *
 * ONE implementation, used by the internal Document workspace and by the client
 * signing portal. It lived inside DocumentWorkspace's SignatureBlock until the
 * portal needed the same thing; copying it would have produced two canvases that
 * drift, which is how this codebase ended up with three renderings of a
 * signature once already.
 *
 * It is deliberately dumb: it draws, it reports whether anything has been drawn,
 * and it hands back a PNG when asked. It knows nothing about documents, blocks,
 * tokens or endpoints, so neither caller has to work around the other's
 * assumptions.
 *
 * TOUCH. The touch listeners are non-passive so the page does not scroll under
 * a finger that is trying to sign — the single most common way signing fails on
 * a phone, which is where most clients open these links.
 */

import React, { useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';

export interface SignaturePadHandle {
  /** PNG data URL, or null when nothing has been drawn. */
  toDataUrl: () => string | null;
  clear: () => void;
  isEmpty: () => boolean;
}

export interface SignaturePadProps {
  width?: number;
  height?: number;
  /** Stroke colour — dark ink, so it stays legible when printed. */
  ink?: string;
  border?: string;
  onDrawnChange?: (drawn: boolean) => void;
}

export const SignaturePad = forwardRef<SignaturePadHandle, SignaturePadProps>(function SignaturePad(
  { width = 400, height = 120, ink = '#1f2733', border = '#e3e6ec', onDrawnChange },
  ref
) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const drawnRef = useRef(false);
  const [, force] = useState(0);

  const setDrawn = (v: boolean) => {
    if (drawnRef.current === v) return;
    drawnRef.current = v;
    onDrawnChange?.(v);
    force((n) => n + 1);
  };

  useImperativeHandle(ref, () => ({
    toDataUrl: () => (drawnRef.current && canvasRef.current ? canvasRef.current.toDataURL('image/png') : null),
    isEmpty: () => !drawnRef.current,
    clear: () => {
      const c = canvasRef.current;
      if (!c) return;
      const ctx = c.getContext('2d');
      if (ctx) ctx.clearRect(0, 0, c.width, c.height);
      setDrawn(false);
    },
  }));

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Retina: back the canvas at device resolution or the stroke is soft, and a
    // soft signature looks like a scan of a photocopy once it is in a PDF.
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = width + 'px';
    canvas.style.height = height + 'px';
    const ctx = canvas.getContext('2d')!;
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = ink;
    setDrawn(false);

    const pos = (e: MouseEvent | TouchEvent) => {
      const r = canvas.getBoundingClientRect();
      const t = (e as TouchEvent).touches?.[0];
      const cx = t ? t.clientX : (e as MouseEvent).clientX;
      const cy = t ? t.clientY : (e as MouseEvent).clientY;
      return { x: cx - r.left, y: cy - r.top };
    };
    const start = (e: MouseEvent | TouchEvent) => {
      drawingRef.current = true;
      const { x, y } = pos(e);
      ctx.beginPath();
      ctx.moveTo(x, y);
    };
    const move = (e: MouseEvent | TouchEvent) => {
      if (!drawingRef.current) return;
      if ((e as TouchEvent).touches) e.preventDefault();   // don't scroll while signing
      const { x, y } = pos(e);
      ctx.lineTo(x, y);
      ctx.stroke();
      setDrawn(true);
    };
    const end = () => { drawingRef.current = false; };

    canvas.addEventListener('mousedown', start);
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', end);
    canvas.addEventListener('touchstart', start, { passive: false });
    canvas.addEventListener('touchmove', move, { passive: false });
    canvas.addEventListener('touchend', end);
    return () => {
      canvas.removeEventListener('mousedown', start);
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', end);
      canvas.removeEventListener('touchstart', start);
      canvas.removeEventListener('touchmove', move);
      canvas.removeEventListener('touchend', end);
    };
  }, [width, height, ink]);

  return (
    <canvas
      ref={canvasRef}
      aria-label="Signature drawing area"
      style={{
        width: `${width}px`, height: `${height}px`, maxWidth: '100%',
        background: '#fff', border: `1px solid ${border}`, borderRadius: '6px',
        touchAction: 'none', cursor: 'crosshair', display: 'block',
      }}
    />
  );
});

/**
 * Initials from a full name — "Jane Alice Doe" -> "JAD".
 *
 * A convenience with a real purpose: DocuSign pre-fills initials from the
 * adopted name and lets the signer correct them, because typing them separately
 * for every initials field is the step people abandon on. Always editable, never
 * assumed.
 */
export function initialsFrom(name: string): string {
  return String(name ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0]!.toUpperCase())
    .join('')
    .slice(0, 5);
}
